import type { VersionView } from '@tabel/shared/schemas/rules';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Ban } from 'lucide-react';
import { useId, useState } from 'react';
import { useToast } from '../../components/ErrorToast';
import { Button, textareaCls } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { ApiError, isTransient } from '../../lib/errors';
import { DialogFooter, GuardedModal, type ModalControl, useDirty } from './GuardedModal';
import { LABEL, VERSION, say } from './texts';

/*
 * Окно «Не вводить правила версии N?» (004, US4, T089; contracts/ui.md, «Окна»; FR-046; сценарий US4 7): у версии
 * «начнёт действовать» — эти правила не начнут действовать, табели не изменятся; «Почему не вводим» обязательно.
 * Ошибка — тост с кодом, введённое остаётся (FR-061).
 */

function NotIntroduceBody({
  version,
  ctl,
  onDone,
}: {
  version: VersionView;
  ctl: ModalControl;
  onDone: () => void;
}) {
  const ids = useId();
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const [text, setText] = useState('');
  useDirty(ctl, text.trim() !== '');

  const save = useMutation({
    mutationFn: () => api.notIntroduce(version.id, text.trim()),
    onSuccess: () => {
      showInfo(VERSION.notIntroduced);
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
      onDone();
    },
    onError: (e) => {
      if (e instanceof ApiError && ['DRAFT_OPEN', 'VERSION_NOT_FUTURE'].includes(e.code))
        void qc.invalidateQueries({ queryKey: rulesKeys.all });
      showError(e, {
        screen: 'Rules',
        action: 'not introduce',
        retry: isTransient(e) ? () => save.mutate() : undefined,
      });
    },
  });
  const reason = text.trim() === '' ? VERSION.whyNotIntroduce : null;

  return (
    <div className="space-y-1.5 text-[13.5px]">
      <label htmlFor={`${ids}-reason`} className="block font-medium text-ink">
        {VERSION.notIntroduceReason}
      </label>
      <textarea
        id={`${ids}-reason`}
        data-autofocus
        rows={3}
        maxLength={500}
        value={text}
        placeholder={VERSION.notIntroducePlaceholder}
        onChange={(e) => setText(e.target.value)}
        className={textareaCls}
      />
      <DialogFooter reasonId={`${ids}-why`} reason={reason}>
        {/* «Закрыть», а не «Отмена»: рядом с «Вернуть правила» и «Не вводить» «Отмена» читается как «отменить версию» (ревью T103, С-2). */}
        <Button onClick={ctl.requestClose}>{LABEL.close}</Button>
        <Button
          variant="danger"
          icon={Ban}
          disabled={reason !== null || save.isPending}
          aria-describedby={reason ? `${ids}-why` : undefined}
          onClick={() => save.mutate()}
        >
          {VERSION.notIntroduceDo}
        </Button>
      </DialogFooter>
    </div>
  );
}

/** Окно «Не вводить» для версии `version` («начнёт действовать»); `null` — окно закрыто. */
export function NotIntroduceDialog({
  version,
  onClose,
  onDone,
}: {
  version: VersionView | null;
  onClose: () => void;
  onDone?: () => void;
}) {
  return (
    <GuardedModal
      open={version !== null}
      onClose={onClose}
      title={version ? say.notIntroduceTitle(version.version) : ''}
      description={version ? say.notIntroduceText(version.validFrom) : undefined}
      width="w-[420px]"
    >
      {(ctl) =>
        version && (
          <NotIntroduceBody
            version={version}
            ctl={ctl}
            onDone={() => {
              ctl.setDirty(false);
              onDone?.();
              onClose();
            }}
          />
        )
      }
    </GuardedModal>
  );
}
